import { useState } from "react";
import { clearSession, listMyTenants, login, saveSession, type Session, type TenantAccess } from "./api/client";
import TwoFactorEnrollment from "./TwoFactor";
import { AuthShell } from "./AuthStage";

/**
 * Sign-in is two steps, because identity and tenant are two different questions
 * (§3.3): first prove who you are, then choose which of your tenants to work in.
 *
 * The console used to ask for a tenant UUID up front, which only worked if you
 * already knew it. /auth/tenants is identity-scoped precisely so this order is
 * possible — a caller cannot name a tenant before being told which ones it may
 * act in. A platform admin gets every tenant; everyone else gets their
 * memberships, and someone with exactly one skips the choice entirely.
 */
export default function SignIn({ onSignedIn }: { onSignedIn: (s: Session) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The second factor, asked for only after the password is accepted.
  const [needsCode, setNeedsCode] = useState(false);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  // Set when a tenant requires a factor this account has not enrolled. The
  // session held here reaches nothing but the enrolment agents.
  const [enrolling, setEnrolling] = useState(false);

  // Set once credentials are accepted and more than one tenant is available.
  const [choice, setChoice] = useState<{ tenants: TenantAccess[]; base: Session } | null>(null);

  async function authenticate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const outcome = await login(email.trim(), password, {
        code: useRecovery ? undefined : code.trim() || undefined,
        recoveryCode: useRecovery ? code.trim() || undefined : undefined,
      });
      if (outcome.kind === "totp") {
        // Not a failure: the password was right and a code is what is left.
        setNeedsCode(true);
        setError(code ? outcome.message : null);
        setCode("");
        setBusy(false);
        return;
      }
      if (outcome.kind === "enroll") {
        saveSession({ tenantId: "", email: email.trim() });
        setEnrolling(true);
        setBusy(false);
        return;
      }
      // No credential is kept: the server set an HttpOnly cookie, and the page
      // holding a second copy of it would undo the point of that.
      const base: Session = { tenantId: "", email: email.trim() };

      // Stored without a tenant so the identity-scoped call below can use it.
      saveSession(base);
      const tenants = await listMyTenants();

      if (tenants.length === 0) {
        clearSession();
        setError("This account has no tenant memberships. An administrator has to grant access before you can sign in.");
        setBusy(false);
        return;
      }
      if (tenants.length === 1) {
        enter(base, tenants[0]!);
        return;
      }
      setChoice({ tenants, base });
      setBusy(false);
    } catch (err) {
      clearSession();
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  function enter(base: Session, t: TenantAccess) {
    const session: Session = {
      ...base,
      tenantId: t.tenant_id,
      tenantName: t.name,
      role: t.role,
 labOnly: t.lab_only ?? false,
      platform: t.platform,
    };
    saveSession(session);
    onSignedIn(session);
  }

  if (enrolling) {
    return (
      <AuthShell>
        <div className="auth-card">
          <h1>Set up two-factor sign-in</h1>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            A tenant you belong to requires two-factor authentication. Enrol an authenticator to finish signing in.
          </p>
          <TwoFactorEnrollment
            onDone={(upgraded) => {
              setEnrolling(false);
              if (!upgraded) {
                // No session came back, so the enrolment one is all there is
                // and it can do nothing else: sign in again, with a code.
                clearSession();
                setNeedsCode(true);
                setCode("");
                setPassword("");
                setError("Two-factor authentication is on. Sign in again with a code from your authenticator.");
                return;
              }
              // Confirming lifted the restriction, so carry straight on into
              // the tenant choice rather than asking for a code that the
              // confirmation has just consumed.
              const base: Session = { tenantId: "", email: email.trim() };
              saveSession(base);
              setBusy(true);
              listMyTenants()
                .then((tenants) => {
                  if (tenants.length === 1) {
                    enter(base, tenants[0]!);
                    return;
                  }
                  setChoice({ tenants, base });
                  setBusy(false);
                })
                .catch((err: unknown) => {
                  clearSession();
                  setError(err instanceof Error ? err.message : String(err));
                  setBusy(false);
                });
            }}
          />
        </div>
      </AuthShell>
    );
  }

  if (choice) {
    return (
      <AuthShell>
        <div className="auth-card">
          <h1>Choose a tenant</h1>
          <p className="muted auth-sub">
            {choice.tenants[0]?.platform
              ? "You are a platform admin, so every tenant is available."
              : "You hold a role in more than one tenant."}
          </p>
          <div className="auth-tenants">
            {choice.tenants.map((t) => (
              <button key={t.tenant_id} onClick={() => enter(choice.base, t)}>
                <span><strong>{t.name}</strong><span className="muted">{t.slug}</span></span>
                <span className="auth-role">{t.role}</span>
              </button>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            <button
              style={{ width: "100%" }}
              onClick={() => {
                clearSession();
                setChoice(null);
              }}
            >
              Back
            </button>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <form className="auth-card" onSubmit={authenticate}>
        <h1>{needsCode ? "One more step" : "Sign in"}</h1>
        <p className="muted auth-sub">
          {needsCode ? "Enter the code from your authenticator to finish signing in." : "to the Heimdall Watchtower console"}
        </p>

        <label htmlFor="signin-email">Email</label>
        <input id="signin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" autoFocus />
        <label htmlFor="signin-password">Password</label>
        <input
          id="signin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
        />
        {needsCode ? (
          <>
            <label htmlFor="signin-code">{useRecovery ? "Recovery code" : "Authenticator code"}</label>
            <input
              id="signin-code"
              value={code}
              onChange={(e) =>
                setCode(useRecovery ? e.target.value.trim() : e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              inputMode={useRecovery ? "text" : "numeric"}
              autoComplete="one-time-code"
              placeholder={useRecovery ? "abcdefgh-ijklmnop" : "123456"}
              autoFocus
            />
            <button
              type="button"
              className="linklike"
              onClick={() => {
                setUseRecovery(!useRecovery);
                setCode("");
              }}
            >
              {useRecovery ? "Use a code from the app" : "Lost your phone? Use a recovery code"}
            </button>
          </>
        ) : null}

        <button className="primary auth-submit" type="submit" disabled={busy}>
          {busy ? "Signing in…" : needsCode ? "Verify and sign in" : "Sign in"}
        </button>
        {error ? (
          <div className="banner error" style={{ marginTop: 14, marginBottom: 0 }}>
            {error}
          </div>
        ) : null}
      </form>
    </AuthShell>
  );
}
